import { Api } from "@/api/Api";
import { SynchrounousState, promiseToAsyncResult } from "@/cache/AsyncResult";
import type { AsyncResult } from "@/cache/AsyncResult";
import type { ColorMapping } from "@/cache/ColorMapCache";
import { ColorMapTexture } from "@/cache/ColorMapCache/ColorMapTexture";
import { networkCaches } from "@/cache/GlobalCache";
import { getCustomColormapRevision } from "@/cache/customColormapRevision";
import type { SceneLayerApi } from "@/layers/SceneLayerApi";
import { unsafeGetColormapByName } from "@/utility/unsafeGetColormapByName";
import { customColormapToSamples } from "./utils";

import { ColorMap } from "@mm/api.meteomatics.com";

export type BinaryColormapRequest = {
  parameter: string;
  colorMapName: string;
  ensSelect?: string;
  calibrated?: boolean;
  verticalInterpolation?: string;
};

export type BinaryColormapRenderInfo = {
  mapping?: ColorMapping;
  usedStale: boolean;
  requestedId: string;
  syncState: unknown;
};

export type FixedBinaryColormap = {
  id: string;
  mapping: ColorMapping;
};

export class BinaryColormapState {
  private colormapCacheId?: string;
  private colormap?: AsyncResult<ColorMapping>;
  private lastLoadedColormap?: { cacheId: string; mapping: ColorMapping };

  constructor(private readonly fixedColormap?: FixedBinaryColormap) {
    // The injected mapping is local, but other colormaps can still be selected at runtime.
    this.colormapCacheId = fixedColormap?.id;
  }

  reset(): void {
    this.colormapCacheId = undefined;
    this.colormap = undefined;
    this.lastLoadedColormap = undefined;
  }

  dispose(gl?: WebGL2RenderingContext): void {
    if (this.fixedColormap && gl) {
      this.fixedColormap.mapping.texture.unload(gl);
    }
    this.reset();
  }

  updateIfNeeded(request: BinaryColormapRequest, scene: SceneLayerApi): void {
    if (this.fixedColormap && this.fixedColormap.id === request.colorMapName) {
      this.colormapCacheId = this.fixedColormap.id;
      this.colormap = undefined;
      return;
    }
    const colorMapName = request.colorMapName ?? "";
    const isCustomColormap =
      colorMapName.trim() !== "" && !Object.prototype.hasOwnProperty.call(ColorMap, colorMapName);

    if (isCustomColormap) {
      const revision = getCustomColormapRevision(colorMapName);
      const cacheId = `${colorMapName}@${revision}`;
      if (this.colormapCacheId === cacheId && this.colormap) {
        return;
      }

      this.colormapCacheId = cacheId;
      const promise = Api.colormaps.colormapsGet({ colormapId: colorMapName }).then((colormap) => {
        const { usualValueRange, samples } = customColormapToSamples(colormap);
        const isSegmented = String(colormap.type ?? "").toLowerCase() === "segmented";
        const texture = new ColorMapTexture(samples, isSegmented);
        return {
          texture,
          recommendedValueRange: usualValueRange,
          colormap: colorMapName,
        };
      });

      this.colormap = promiseToAsyncResult(promise);

      if (this.colormap.synchronous === SynchrounousState.StillPending) {
        this.colormap.asynchronous.then(() => scene.repaint()).catch(() => undefined);
      }
      return;
    }

    const style: ColorMap = unsafeGetColormapByName(request.colorMapName, request.parameter as any);
    const cacheId = `${style}@${request.parameter}@${request.ensSelect || ""}@${request.calibrated ? "cal" : "raw"}@${
      request.verticalInterpolation || ""
    }`;

    const sync: unknown = this.colormap?.synchronous;
    const isNotRequested = sync === SynchrounousState.NotRequested || sync === 0;

    if (this.colormapCacheId === cacheId && this.colormap && !isNotRequested) {
      return;
    }

    this.colormapCacheId = cacheId;
    this.colormap = networkCaches.colormap_cache.retrieveColorMap({
      parameter: request.parameter,
      style,
      ensSelect: request.ensSelect || undefined,
      calibrated: request.calibrated,
      vertical_interpolation: request.verticalInterpolation as any,
    } as any);

    if (this.colormap && this.colormap.synchronous === SynchrounousState.StillPending) {
      this.colormap.asynchronous.then(() => scene.repaint()).catch(() => undefined);
    }
  }

  isPending(): boolean {
    if (this.fixedColormap && this.fixedColormap.id === this.colormapCacheId) {
      return false;
    }
    return this.colormap?.synchronous === SynchrounousState.StillPending;
  }

  getRenderInfo(): BinaryColormapRenderInfo {
    const fixedColormap = this.fixedColormap;
    if (fixedColormap && fixedColormap.id === this.colormapCacheId) {
      return {
        mapping: fixedColormap.mapping,
        usedStale: false,
        requestedId: fixedColormap.id,
        syncState: fixedColormap.mapping,
      };
    }
    const requestedId = this.colormapCacheId ?? "";
    const sync: unknown = this.colormap?.synchronous;
    const hasLoadedColormap =
      !!sync && typeof sync === "object" && "recommendedValueRange" in sync && "texture" in sync;

    if (hasLoadedColormap) {
      const loaded = sync as ColorMapping;
      this.lastLoadedColormap = { cacheId: requestedId, mapping: loaded };
      return { mapping: loaded, usedStale: false, requestedId, syncState: sync };
    }

    if (this.lastLoadedColormap?.mapping) {
      return {
        mapping: this.lastLoadedColormap.mapping,
        usedStale: true,
        requestedId,
        syncState: sync,
      };
    }

    return { mapping: undefined, usedStale: false, requestedId, syncState: sync };
  }
}
